/**
 * Client mirror of the server's compensation write rule, so the UI never offers an
 * edit the server refuses. Server side: SAL_UPDATE / APP_UPDATE in
 * server/constants/permissions.js and canWriteCompensation() in server/middleware/policy.js.
 *
 * Compensation is never self-service. Every employee holds salaries:update_own and
 * appraisals:update_own through the seeded `employee` role, so the generic
 * AuthContext.hasUpdatePermission() (update_all OR update_own) must not gate pay edits.
 * appraisals:update_own grants exactly one write, the self-review on AppraisalDetail.
 *
 * Each helper takes AuthContext's `hasPermission(resource, action)`.
 */

/** Salary edits: update_all only, which is also the self-rule exemption, so no per-record check is needed. */
export const canEditSalaries = (hasPermission) => hasPermission('salaries', 'update_all');

/** Appraisal edits, before a record is known: HR/admin (update_all) or department managers (update_reports). */
export const canEditAppraisals = (hasPermission) =>
  hasPermission('appraisals', 'update_all') || hasPermission('appraisals', 'update_reports');

/**
 * The server's self rule for any pay write (create, edit, activate/deactivate): only
 * <resource>:update_all may write the caller's own record, and below update_all a
 * caller with no linked employee record may write none. read_all and delete also
 * resolve to scope `all` on the server, but they are deliberately NOT an exemption:
 * a read_all + create role could otherwise raise its own pay.
 *
 * @param {Function} hasPermission AuthContext's hasPermission.
 * @param {'salaries'|'appraisals'} resource
 * @param {{ employee_id?: number|null }|null} user The signed-in user.
 * @param {number|string} employeeId The employee the write is for.
 * @returns {boolean}
 */
export const canWriteCompensationFor = (hasPermission, resource, user, employeeId) => {
  if (hasPermission(resource, 'update_all')) return true;
  if (user?.employee_id == null) return false;
  return String(employeeId) !== String(user.employee_id);
};

/**
 * Appraisal edits for one record: the grant check plus the self rule, so a manager
 * holding update_reports cannot edit (or activate / deactivate) their own appraisal.
 *
 * @param {Function} hasPermission AuthContext's hasPermission.
 * @param {{ employee_id?: number|null }|null} user The signed-in user.
 * @param {{ employee_id?: number|string }|null} appraisal The record being edited.
 * @returns {boolean}
 */
export const canEditAppraisal = (hasPermission, user, appraisal) =>
  canEditAppraisals(hasPermission)
  && canWriteCompensationFor(hasPermission, 'appraisals', user, appraisal?.employee_id);
